import { useState, useEffect, useCallback, useRef } from 'react';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';

import { Button, DatePicker, ConfigProvider, Tooltip } from 'antd';
import Icon, {
  CaretLeftOutlined,
  CaretRightOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

import ReplayIcon from '@/assets/pictos/replay.svg?react';
import useP4RContext from '@/store/P4RContext';
import { useGetAppConfig, useGetHorodateTime } from '@/data/hooks';
import { createStartDate } from '@/utils/dateUtils';
import appTheme from '@/styles/theme';

import GlobalDatePickerStyle from './GlobalDatePickerStyle';
import DateListPanel from './DateListPanel';

import styles from './style.module.scss';

// Type definition based on Ant Design's components.date API
export type DateComponent = Required<
  NonNullable<React.ComponentProps<typeof DatePicker>['components']>
>['date'];
export type GetProps<T> = T extends React.ComponentType<infer P> ? P : never;

type DatePickerPanelMode =
  | 'time'
  | 'date'
  | 'week'
  | 'month'
  | 'quarter'
  | 'year'
  | 'decade';

export interface DateSelectorProps {
  timezone?: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  onChangeDate?: (date: Dayjs) => void;
  disableTime?: boolean;
  customTitle?: React.ReactNode;
  dateSuffix?: React.ReactNode;
  highlightedDays?: {
    dates: string[];
    legend?: string;
  };
  displayAsList?: boolean;
}

function DateSelector({
  timezone = '+00:00',
  minDate: customMinDate,
  maxDate: customMaxDate,
  onChangeDate,
  disableTime,
  customTitle,
  dateSuffix,
  highlightedDays,
  displayAsList,
}: DateSelectorProps) {
  const { timelineMode, changeTimelineMode, changeStartDate, startDate } =
    useP4RContext();
  const { data: appConfig, isLoading: appConfigLoading } = useGetAppConfig();
  const { data: horodateTime } = useGetHorodateTime(true);

  // Check if we're in custom mode (when dateSelectorOptions is provided)
  const isCustomMode = !!(
    customMinDate ||
    customMaxDate ||
    onChangeDate ||
    disableTime !== undefined
  );

  // In custom mode, treat as replay mode for interaction purposes
  const displayMode = isCustomMode ? 'replay' : timelineMode;

  // Always use context date for display - single source of truth!
  const replayDateTime = startDate?.[0];

  // Local state for date picker
  const [datePickerValue, setDatePickerValue] = useState<Dayjs | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [datePickerMode, setDatePickerMode] =
    useState<DatePickerPanelMode>('date');
  const preventNextTriggerClick = useRef(false);
  const openDatePicker = () => {
    if (!preventNextTriggerClick.current) {
      setDatePickerOpen(true);
    } else {
      preventNextTriggerClick.current = false;
    }
  };

  // In custom mode, track the current selected date
  const [customSelectedDate, setCustomSelectedDate] = useState<Dayjs | null>(
    null,
  );

  // In custom mode, use custom selected date, otherwise use context date
  const currentDisplayDate = isCustomMode ? customSelectedDate : replayDateTime;

  // Date boundaries - use custom dates if provided, otherwise use defaults
  const minDate =
    customMinDate ||
    (appConfig?.data.replay.min_datetime &&
      dayjs.utc(appConfig?.data.replay.min_datetime));

  const maxDate =
    customMaxDate ||
    dayjs().utcOffset(timezone).startOf('hour').subtract(1, 'hour');

  // Initialize custom selected date when entering custom mode
  useEffect(() => {
    if (isCustomMode && !customSelectedDate && customMinDate) {
      setCustomSelectedDate(customMinDate);
    }
  }, [isCustomMode, customSelectedDate, customMinDate]);

  // Update context when date/time changes in replay mode
  const updateContext = (dateTime: Dayjs) => {
    if (onChangeDate) {
      // Use custom callback if provided
      onChangeDate(dateTime);
    } else {
      // Use default context update
      changeStartDate(createStartDate({ startDayjs: dateTime, timezone }));
    }
  };

  // Navigation functions
  const goToPreviousDay = () => {
    const currentDate = isCustomMode ? customSelectedDate : replayDateTime;
    if (!currentDate) return;
    const newDate = currentDate.subtract(1, 'day');
    if (isCustomMode) {
      setCustomSelectedDate(newDate);
      updateContext(newDate);
    } else {
      updateContext(newDate);
      setDatePickerValue(newDate);
    }
  };
  const goToNextDay = () => {
    const currentDate = isCustomMode ? customSelectedDate : replayDateTime;
    if (!currentDate) return;
    const newDate = currentDate.add(1, 'day');
    if (isCustomMode) {
      setCustomSelectedDate(newDate);
      updateContext(newDate);
    } else {
      updateContext(newDate);
      setDatePickerValue(newDate);
    }
  };
  const goToPreviousHour = () => {
    if (!replayDateTime || disableTime) return;
    const newDateTime = replayDateTime.subtract(1, 'hour');
    updateContext(newDateTime);
    setDatePickerValue(newDateTime);
  };
  const goToNextHour = () => {
    if (!replayDateTime || disableTime) return;
    const newDateTime = replayDateTime.add(1, 'hour');
    updateContext(newDateTime);
    setDatePickerValue(newDateTime);
  };

  // Boundary checking functions for button states
  const canGoToPreviousDay = () => {
    const currentDate = isCustomMode ? customSelectedDate : replayDateTime;
    if (!currentDate) return false;
    const previousDay = currentDate.subtract(1, 'day');
    return previousDay.isAfter(minDate) || previousDay.isSame(minDate, 'day');
  };
  const canGoToNextDay = () => {
    const currentDate = isCustomMode ? customSelectedDate : replayDateTime;
    if (!currentDate) return false;
    const nextDay = currentDate.add(1, 'day');
    return nextDay.isBefore(maxDate) || nextDay.isSame(maxDate, 'day');
  };
  const canGoToPreviousHour = () => {
    if (!replayDateTime || disableTime) return false;
    const previousHour = replayDateTime.subtract(1, 'hour');
    return previousHour.isAfter(minDate) || previousHour.isSame(minDate);
  };
  const canGoToNextHour = () => {
    if (!replayDateTime || disableTime) return false;
    const nextHour = replayDateTime.add(1, 'hour');
    return nextHour.isBefore(maxDate) || nextHour.isSame(maxDate);
  };

  // Mode toggle
  const handleReplayClick = () => {
    // Set DatePicker value to max allowed date (1 hour ago)
    setDatePickerValue(maxDate);
    // Open DatePicker immediately
    openDatePicker();
  };

  const handleBackToLive = () => {
    changeTimelineMode('live');
    // Update context to current time - display will auto-update!
    changeStartDate(createStartDate({ timezone }));
    // Close DatePicker if open and reset value
    setDatePickerOpen(false);
    setDatePickerValue(null);
  };

  /**
   * When a mousedown event (happening before click) is detected on a button that is supposed control the open state of the date picker, we want to prevent the following click event from changing the picker state back to true (since a mousedown outside of it will first close it).
   */
  const handleTriggerMousedown = () => {
    if (datePickerOpen) {
      preventNextTriggerClick.current = true; // Prevent the click from changing the open state of the date picker back to true
    }
  };

  const handleDatePickerButtonClick = () => {
    openDatePicker();
  };

  const handleDatePickerOpenChange = (open: boolean) => {
    setDatePickerOpen(open);
    if (!open) {
      // Reset the date picker mode to "date" when the date picker is closed
      setDatePickerMode('date');
    }
  };

  /**
   * When the date picker panel changes, we want to force it to open in "month" mode when the target mode is any other than "date"
   * For instance, if the user navigates to year mode (when clicking on the year button of the date picker panel header),
   * we want to force it to open in month mode instead.
   */
  const handleDatePickerPanelChange = (_: Dayjs, mode: DatePickerPanelMode) => {
    setDatePickerMode(mode === 'date' ? 'date' : 'month');
  };

  // Date picker change handler
  const handleDatePickerChange = (date: Dayjs | null) => {
    if (!date) return;
    if (isCustomMode) {
      // In custom mode, update custom selected date
      setCustomSelectedDate(date);
      updateContext(date);
    } else {
      // Switch to replay mode (might already be in replay mode)
      changeTimelineMode('replay');
      // Set DatePicker value to selected date
      setDatePickerValue(date);
      // Update context with selected date
      updateContext(date);
    }
    // Close DatePicker
    setDatePickerOpen(false);
  };

  // Dynamic disabled time function
  const getDisabledTime = (date: Dayjs) => {
    const selectedDate = date.utcOffset(timezone);

    // If the selected date is today, disable future hours
    if (selectedDate.isSame(maxDate, 'day')) {
      const currentHour = maxDate.hour();
      return {
        disabledHours: () => {
          const hours = [];
          for (let i = currentHour + 1; i < 24; i += 1) {
            hours.push(i);
          }
          return hours;
        },
      };
    }
    // For past dates, all hours are available
    return {};
  };

  // Create adapter component that injects additional props
  const DateListPanelAdapter = useCallback(
    (props: GetProps<DateComponent>) =>
      displayAsList ? (
        <DateListPanel
          {...props}
          minDate={minDate}
          maxDate={maxDate}
          highlightedDays={highlightedDays}
        />
      ) : null,
    [displayAsList, minDate, maxDate, highlightedDays],
  );

  // Format functions
  const formatDate = (date: Dayjs) => date.format('DD MMMM YYYY');
  const formatHour = (date: Dayjs) => date.format('HH[h]mm');

  if (appConfigLoading) {
    return false;
  }

  return (
    <div className="DateSelector">
      <div className="content-container">
        <div className="title-container">
          <h4 className="title">
            {displayMode === 'live'
              ? 'Weather Forecast from'
              : customTitle || 'Replay'}
          </h4>
          {displayMode === 'replay' && !isCustomMode && (
            <p className="forecast-info">
              <span>Weather Forecast</span>{' '}
              <Tooltip
                title={
                  !horodateTime?.weatherForecastHorodatetime ? null : (
                    <div style={{ fontSize: 10, lineHeight: 1.2 }}>
                      Weather Forecast from
                      <br />
                      {dayjs
                        .utc(horodateTime?.weatherForecastHorodatetime)
                        .format('dddd D MMMM YYYY HH:mm')}
                    </div>
                  )
                }
                placement="bottom"
              >
                <Icon component={InfoCircleOutlined} />
              </Tooltip>
            </p>
          )}
        </div>
        <div className="controls-container">
          <div className="control">
            <Button
              icon={<CaretLeftOutlined />}
              type="link"
              size="small"
              onClick={goToPreviousDay}
              disabled={!canGoToPreviousDay()}
              className={classNames({
                'arrow-button': true,
                hidden: displayMode === 'live',
              })}
            />
            {displayMode === 'live' ? (
              <span className="display">
                {horodateTime?.weatherForecastHorodatetime &&
                  formatDate(
                    dayjs.utc(horodateTime.weatherForecastHorodatetime),
                  )}
                {dateSuffix}
              </span>
            ) : (
              <span className="display">
                {currentDisplayDate && formatDate(currentDisplayDate)}
                {dateSuffix}
              </span>
            )}
            <Button
              icon={<CaretRightOutlined />}
              type="link"
              size="small"
              onClick={goToNextDay}
              disabled={!canGoToNextDay()}
              className={classNames({
                'arrow-button': true,
                hidden: displayMode === 'live',
              })}
            />
          </div>

          {!disableTime && (
            <div className="control">
              {displayMode === 'replay' && (
                <Button
                  icon={<CaretLeftOutlined />}
                  type="link"
                  size="small"
                  onClick={goToPreviousHour}
                  disabled={!canGoToPreviousHour()}
                  className={classNames({
                    'arrow-button': true,
                    // hidden: displayMode === 'live',
                  })}
                />
              )}
              {displayMode === 'live' ? (
                <span className="display">
                  {horodateTime?.weatherForecastHorodatetime &&
                    formatHour(
                      dayjs.utc(horodateTime.weatherForecastHorodatetime),
                    )}
                </span>
              ) : (
                <span className="display">
                  {currentDisplayDate && formatHour(currentDisplayDate)}
                </span>
              )}
              {displayMode === 'replay' && (
                <Button
                  icon={<CaretRightOutlined />}
                  type="link"
                  size="small"
                  onClick={goToNextHour}
                  disabled={!canGoToNextHour()}
                  className={classNames({
                    'arrow-button': true,
                    // hidden: displayMode === 'live',
                  })}
                />
              )}
            </div>
          )}
          {displayMode === 'replay' && (
            <Button
              className="calendar-picker-button"
              type="link"
              size="small"
              icon={<CalendarOutlined />}
              onClick={handleDatePickerButtonClick}
              onMouseDown={handleTriggerMousedown}
            />
          )}
        </div>
      </div>

      {/* Mode Toggle Button - hidden in custom mode */}
      {!isCustomMode && (
        <ConfigProvider
          theme={{
            token: {
              colorPrimary:
                displayMode === 'live'
                  ? appTheme.token.colorSecondary
                  : appTheme.token.colorPrimary,
            },
          }}
        >
          <Button
            type="primary"
            onClick={
              displayMode === 'live' ? handleReplayClick : handleBackToLive
            }
            onMouseDown={handleTriggerMousedown}
            className="mode-button"
          >
            {displayMode === 'live' ? (
              <>
                <div>
                  Replay
                  <br />
                  Past
                </div>
                <Icon component={ReplayIcon} />
              </>
            ) : (
              <>
                Back <br />
                to Live <br />
              </>
            )}
          </Button>
        </ConfigProvider>
      )}
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: appTheme.token.colorSecondary,
            controlItemBgActive: appTheme.token.colorLightSecondary,
          },
        }}
      >
        <GlobalDatePickerStyle highlightedDays={highlightedDays} />
        <DatePicker
          value={isCustomMode ? customSelectedDate : datePickerValue}
          defaultValue={
            isCustomMode
              ? minDate
              : dayjs().utcOffset(timezone).subtract(1, 'hour')
          }
          open={datePickerOpen}
          onChange={handleDatePickerChange}
          onOk={handleDatePickerChange}
          onOpenChange={handleDatePickerOpenChange}
          mode={datePickerMode}
          onPanelChange={handleDatePickerPanelChange}
          disabledDate={(date) => {
            const dateInTimezone = date.utcOffset(timezone);
            return (
              dateInTimezone.isBefore(minDate) ||
              dateInTimezone.isAfter(maxDate)
            );
          }}
          disabledTime={getDisabledTime}
          showTime={disableTime ? false : { format: 'HH' }}
          showNow={false}
          format="YYYY-MM-DD HH"
          placeholder="Select date and time"
          className="calendar-picker"
          classNames={{
            popup: {
              root: styles.datepickerPopup,
            },
          }}
          placement="bottomRight"
          components={
            displayAsList && customMinDate && customMaxDate && disableTime
              ? {
                  date: DateListPanelAdapter,
                }
              : undefined
          }
          renderExtraFooter={() => (
            <>
              {highlightedDays?.legend && (
                <div className={styles.highlightedDaysLegend}>
                  {highlightedDays.legend}
                </div>
              )}
              <div className={styles.replayModeLegend}>
                <InfoCircleOutlined />
                <span className={styles.text}>
                  P4R will show the weather & fault forecasts that were
                  displayed at the selected date and time
                </span>
              </div>
            </>
          )}
        />
      </ConfigProvider>
    </div>
  );
}

export default DateSelector;
